import React, { useState } from 'react';

interface MailComposerProps {
  apiBaseUrl: string;
  adminKey: string;
  onSent?: () => void;
}

export const MailComposer: React.FC<MailComposerProps> = ({ apiBaseUrl, adminKey, onSent }) => {
  const [title, setTitle] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [messageType, setMessageType] = useState<string>('system');
  const [isSending, setIsSending] = useState<boolean>(false);
  const [lastResult, setLastResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleSend = async (): Promise<void> => {
    if (!title.trim() || !message.trim()) {
      setLastResult({ success: false, message: 'Title and message are required' });
      return;
    }

    setIsSending(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/prod/send-mail-to-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey,
        },
        body: JSON.stringify({
          title: title.trim(),
          message: message.trim(),
          messageType: messageType,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setLastResult({ success: true, message: data.message || 'Mail sent successfully' });
        setTitle('');
        setMessage('');
        onSent?.();
      } else {
        setLastResult({ success: false, message: data.error || 'Failed to send mail' });
      }
    } catch (error) {
      setLastResult({ success: false, message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#1a1a2e', border: '1px solid #4a5568', padding: 20 }}>
      <h2 style={{ color: '#ffa500', fontSize: 16, marginBottom: 16, margin: 0 }}>MAIL COMPOSER</h2>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={{ color: '#a0aec0', fontSize: 11, display: 'block', marginBottom: 6 }}>
            MESSAGE TYPE
          </label>
          <select
            value={messageType}
            onChange={(e) => setMessageType(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              backgroundColor: '#2d3748',
              border: '1px solid #4a5568',
              color: '#e2e8f0',
              fontSize: 12,
              fontFamily: 'inherit',
            }}
          >
            <option value="system">SYSTEM</option>
            <option value="order_filled">ORDER FILLED</option>
            <option value="territory_lost">TERRITORY LOST</option>
            <option value="courier_expired">COURIER EXPIRED</option>
            <option value="quest_complete">QUEST COMPLETE</option>
          </select>
        </div>

        <div>
          <label style={{ color: '#a0aec0', fontSize: 11, display: 'block', marginBottom: 6 }}>
            TITLE
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder="Enter message title..."
            style={{
              width: '100%',
              padding: '8px 12px',
              backgroundColor: '#2d3748',
              border: '1px solid #4a5568',
              color: '#e2e8f0',
              fontSize: 12,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div>
          <label style={{ color: '#a0aec0', fontSize: 11, display: 'block', marginBottom: 6 }}>
            MESSAGE
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder="Enter message content..."
            style={{
              width: '100%',
              padding: '8px 12px',
              backgroundColor: '#2d3748',
              border: '1px solid #4a5568',
              color: '#e2e8f0',
              fontSize: 12,
              fontFamily: 'inherit',
              minHeight: 120,
              boxSizing: 'border-box',
              resize: 'vertical',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleSend}
            disabled={isSending || !title.trim() || !message.trim()}
            style={{
              flex: 1,
              padding: '10px 16px',
              backgroundColor: isSending || !title.trim() || !message.trim() ? '#1f2937' : '#238636',
              color: isSending || !title.trim() || !message.trim() ? '#718096' : '#fff',
              border: '1px solid #238636',
              fontSize: 12,
              fontWeight: 600,
              cursor: isSending || !title.trim() || !message.trim() ? 'default' : 'pointer',
              fontFamily: 'inherit',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            {isSending ? 'SENDING...' : 'SEND TO ALL PLAYERS'}
          </button>
        </div>

        {lastResult && (
          <div
            style={{
              padding: 12,
              backgroundColor: lastResult.success ? '#0d3d0d' : '#3d0d0d',
              border: `1px solid ${lastResult.success ? '#238636' : '#742a2a'}`,
              color: lastResult.success ? '#22c55e' : '#ef4444',
              fontSize: 11,
              fontFamily: 'inherit',
            }}
          >
            {lastResult.message}
          </div>
        )}
      </div>
    </div>
  );
};
